import { createSlice } from "@reduxjs/toolkit";
import buildAction from "../../helpers/buildAction.ts";
import thunks from "./thunks";
import {
  IStoreSlice,
  TGlobalComponents,
  TSiteVersion,
} from "@/redux/slices/store/types";
import {
  BreakpointKey,
  ColorKey,
  Colors,
  ColorValue,
  ImportTheme,
} from "@template/theme";
import yay from "@template/theme/themes/yay.ts";

export const initialState: IStoreSlice = {
  reviews: {
    loaded: false,
    reviews: {
      total: "0",
      average: "5",
      five_start_count: "5",
      comments: [],
    },
  },
  articles: {
    loaded: false,
    articles: [],
  },
  plan_prices: {
    loaded: true,
    plans: [],
    number_costs: {},
  },
  tlds: {
    tlds: {
      loaded: false,
      tlds: [],
    },
    prices: {
      loaded: false,
      prices: [],
    },
  },
  site_version: {
    id: 1,
    currency: "GBP",
    loading: false,
  },
  // Initialise with the full default theme (including colors) so that
  // StoreThemeContextProvider can render during SSR. Without colors in the
  // initial state, the theme is only set client-side (ThemeSetter's
  // useEffect), so the server HTML contains no page content and non-JS
  // crawlers (GPTBot, ClaudeBot, etc.) see an empty page.
  theme: {
    color_algorithm: "auto",
    ...yay,
  } as ImportTheme,
  global_components: {
    header: {
      content: {},
    },
    footer: {
      content: {},
    },
  },
};

export const storeSlice = createSlice({
  name: "storeSlice",
  initialState,
  reducers: {
    setSiteVersion: (
      state: IStoreSlice,
      { payload }: { payload: TSiteVersion }
    ) => {
      state.site_version = payload;
    },
    setTheme: (state: IStoreSlice, { payload }: { payload: ImportTheme }) => {
      state.theme = {
        ...state.theme,
        ...payload,
      };
    },
    setThemeFont: (state: IStoreSlice, { payload }: { payload: string }) => {
      state.theme.fontFamily = payload;
    },
    setThemeColors: (
      state: IStoreSlice,
      {
        payload,
      }: {
        payload: Partial<Colors>;
      }
    ) => {
      state.theme.colors = {
        ...state.theme.colors,
        ...payload,
      };
    },
    setThemeColorByKey: (
      state: IStoreSlice,
      {
        payload,
      }: {
        payload: {
          color: ColorKey;
          // key is key in TColorType
          key: keyof ColorValue;
          value: string;
        };
      }
    ) => {
      state.theme.colors[payload.color][payload.key] = payload.value;
    },
    setThemeBreakpoints: (
      state: IStoreSlice,
      {
        payload,
      }: {
        payload: Record<BreakpointKey, number>;
      }
    ) => {
      state.theme.breakpoints = payload;
    },
    setThemeBreakpoint: (
      state: IStoreSlice,
      {
        payload,
      }: {
        payload: {
          breakpoint: BreakpointKey;
          value: number;
        };
      }
    ) => {
      state.theme.breakpoints[payload.breakpoint] = payload.value;
    },
    setThemeContent: (
      state: IStoreSlice,
      { payload }: { payload: ImportTheme["content"] }
    ) => {
      state.theme.content = payload;
    },
    setThemeContentMaxWidth: (
      state: IStoreSlice,
      { payload }: { payload: number }
    ) => {
      state.theme.content.maxWidth = payload;
    },
    setGlobalComponents: (
      state: IStoreSlice,
      {
        payload,
      }: {
        payload: TGlobalComponents;
      }
    ) => {
      state.global_components = payload;
    },
    setColorAlgorithm: (
      state: IStoreSlice,
      {
        payload,
      }: {
        payload: ImportTheme["color_algorithm"];
      }
    ) => {
      state.theme.color_algorithm = payload;
    },
  },
  extraReducers: (builder) => {
    thunks.forEach((thunk) => buildAction(builder, thunk));
  },
});

export default storeSlice.reducer;
export const {
  setSiteVersion,
  setTheme,
  setThemeFont,
  setThemeColors,
  setThemeColorByKey,
  setThemeBreakpoints,
  setThemeBreakpoint,
  setThemeContent,
  setThemeContentMaxWidth,
  setGlobalComponents,
  setColorAlgorithm,
} = storeSlice.actions;
